import {
    Header,
    Table,
    Box,
    SpaceBetween,
    Button,
    Container,
    Pagination,
    type TableProps,
    CollectionPreferences,
    TextFilter,
} from '@amzn/awsui-components-console';
import { useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { useGetClassroom, useGetLearningActivity } from '@/api/hooks';
import messages from '@/components/AttendanceTable/messages';
import type {
    AttendanceRecord,
    AttendanceTableItem,
    BasicCustomer,
} from '@/components/AttendanceTable/types';
import {
    convertAttendanceRecordsToTableItems,
    supportsAttendance,
} from '@/components/AttendanceTable/utils';
import { LinkButton } from '@/components/LinkButton';
import { useAttendanceCollection } from '@/data/useAttendanceCollection';
import { useAttendanceData } from '@/data/useAttendanceData';
import { paths } from '@/routing/paths';

const columnDefinitions: TableProps['columnDefinitions'] = [
    {
        id: 'name',
        header: 'Name',
        cell: ({ fullName }: AttendanceTableItem) => fullName,
        sortingField: 'fullName',
    },
    {
        id: 'email',
        header: 'Email',
        cell: ({ email }: AttendanceTableItem) => email,
        isRowHeader: true,
        sortingField: 'email',
    },

    {
        id: 'customer',
        header: 'Customer',
        cell: ({ customer }: AttendanceTableItem) => customer,
    },
    {
        id: 'subscription',
        header: 'Subscription',
        cell: ({ subscription }: AttendanceTableItem) => <FormattedMessage {...subscription} />,
    },
    {
        id: 'attendance',
        header: 'Attendance status',
        cell: ({ attendanceStatus }: AttendanceTableItem) => (
            <FormattedMessage {...attendanceStatus} />
        ),
    },
    {
        id: 'evaluation',
        header: 'Evaluation status',
        cell: ({ evaluationStatus }: AttendanceTableItem) => (
            <FormattedMessage {...evaluationStatus} />
        ),
    },
];

export const AttendanceTable = () => {
    const { formatMessage } = useIntl();
    const { data: classroomData } = useGetClassroom();
    const { data: learningActivity, refetch: refetchActivity } = useGetLearningActivity();

    // Transform classroomData to match the format expected by data hook
    const classroomForHook = useMemo(() => {
        return classroomData?.getClassroom || null;
    }, [classroomData?.getClassroom]);

    // Retrieve all data 1 page at a time
    const {
        attendanceRecords,
        error,
        isLoading,
        refetch: refetchClassAttendance,
    } = useAttendanceData(classroomForHook);

    const isLoadingAttendance = isLoading && (!attendanceRecords || attendanceRecords.length === 0);

    const items = useMemo(() => {
        const records = (attendanceRecords ?? []) as AttendanceRecord[];
        const allCustomers = (learningActivity?.getLearningActivity.customers ??
            []) as BasicCustomer[];
        return convertAttendanceRecordsToTableItems({ records, customers: allCustomers });
    }, [attendanceRecords, learningActivity]);

    const filterFields = columnDefinitions.map((col) => col.sortingField) as string[];

    // Configure table settings (pageSize, pagination, etc) with filtering
    const { displayItems, tableSettings } = useAttendanceCollection(items, {
        filtering: {
            fields: filterFields,
        },
    });

    const refetchAllActivityData = () => {
        refetchActivity();
        refetchClassAttendance();
    };

    // don't render in the event the class is not an instance of an activity
    if (!supportsAttendance(learningActivity?.getLearningActivity.lms_type)) return null;

    const header = (
        <Header
            actions={
                <SpaceBetween direction='horizontal' size='s'>
                    <Button
                        data-testid='attendance-refresh'
                        iconName='refresh'
                        iconAlt={formatMessage(messages.refresh)}
                        ariaLabel={formatMessage(messages.refresh)}
                        loading={isLoadingAttendance}
                        onClick={refetchAllActivityData}
                        disabled={isLoading}
                    />
                    <LinkButton
                        data-testid='attendance-page-link'
                        to={paths.classAttendance(
                            classroomData?.getClassroom.classroom?.classroomId as string,
                        )}
                        iconAlign='right'
                        iconName='external'
                    >
                        {formatMessage(messages.markAttendance)}
                    </LinkButton>
                </SpaceBetween>
            }
        >
            {formatMessage(messages.header, { rosterSize: items.length })}
        </Header>
    );

    return error ? (
        <Container header={header}>
            <Box margin={{ vertical: 's' }} textAlign='center'>
                {formatMessage(messages.attendanceLoadErrorContent)}
            </Box>
        </Container>
    ) : (
        <Table
            {...tableSettings.collectionProps}
            data-testid='attendance-table'
            header={header}
            renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
                formatMessage(messages.itemsDisplayedAriaLabel, {
                    firstIndex,
                    lastIndex,
                    totalItemsCount,
                })
            }
            enableKeyboardNavigation
            loadingText={formatMessage(messages.loading)}
            loading={isLoadingAttendance}
            items={displayItems}
            columnDefinitions={columnDefinitions}
            pagination={<Pagination {...tableSettings.paginationProps} />}
            filter={<TextFilter {...tableSettings.filterProps} />}
            preferences={<CollectionPreferences {...tableSettings.collectionPreferenceProps} />}
        />
    );
};
